'use client';

import {
  attendantFilterAgeFrom,
  attendantFilterAgeTo,
  attendantFilterBreastScope,
  attendantFilterClothesSize,
  attendantFilterCustomers,
  attendantFilterHairColor,
  attendantFilterHeightFrom,
  attendantFilterHeightTo,
  attendantFilterLanguages,
  attendantFilterLocations,
  attendantFilterMobility,
  attendantFilterOrigin,
  attendantFilterPreferences,
  attendantFilterServices,
  attendantFilterSexuality,
  attendantFilterSiliconNo,
  attendantFilterSiliconYes,
  attendantFilterSmoker,
  attendantFilterToys,
  attendantFilterTravelCompanion,
  attendantSexualOrientationBisexual,
  attendantSexualOrientationHetero,
  attendantSexualOrientationHomosexual,
} from '@bells/i18n';
import { type Locale } from '@bells/i18n/runtime';
import countries from '@bells/iso/countries';
import languages from '@bells/iso/languages';
import {
  GosStatuses,
  MobilityStatuses,
  NoCustomerStatuses,
  Piercings,
  SmokerStatuses,
  Tattoos,
  ThreesomeStatuses,
  Toys,
  TravelCompanion,
  VisitStatuses,
} from '@bells/types/attendant';
import { X } from 'lucide-react';
import { useMemo } from 'react';
import { Badge } from '../../components/badge';
import { Button } from '../../components/button';
import { HairColors } from '../../shared/attendant-types';
import { useFilter } from './filter-provider';
import {
  AttendantSearchParams,
  maximalAge,
  maximalHeight,
  minimalAge,
  minimalHeight,
} from './utils';
import { cn } from '@bells/core/lib/utils';
export default function ActiveFiltersBadges({
  className,
}: {
  className?: string;
}) {
  const { filters, setFilters, locale } = useFilter();

  const labelMap: Partial<Record<keyof AttendantSearchParams, string>> =
    useMemo(
      () => ({
        ageFrom: attendantFilterAgeFrom(),
        ageTo: attendantFilterAgeTo(),
        heightFrom: attendantFilterHeightFrom(),
        heightTo: attendantFilterHeightTo(),
        smoker: attendantFilterSmoker(),
        hairColors: attendantFilterHairColor(),
        gos: attendantFilterPreferences(),
        toys: attendantFilterToys(),
        customers: attendantFilterCustomers(),
        visits: attendantFilterLocations(),
        mobility: attendantFilterMobility(),
        services: attendantFilterServices(),
        origins: attendantFilterOrigin(),
        languages: attendantFilterLanguages(),
        clothesSize: attendantFilterClothesSize(),
        breastScope: attendantFilterBreastScope(),
        sexualOrientation: attendantFilterSexuality(),
        piercings: 'Piercings',
        tattoos: 'Tattoos',
        hasSilicon: 'Silicon',
        travelCompanion: attendantFilterTravelCompanion(),
      }),
      [locale]
    );

  const arrayLabelMap: Record<string, { value: string; label: string }[]> =
    useMemo(
      () => ({
        hairColors: HairColors(locale),
        services: ThreesomeStatuses(locale).map((s) => ({
          value: s.value,
          label: s.label,
        })),
        sexualOrientation: [
          { value: 'hetero', label: attendantSexualOrientationHetero() },
          { value: 'bisexual', label: attendantSexualOrientationBisexual() },
          {
            value: 'homosexual',
            label: attendantSexualOrientationHomosexual(),
          },
        ],
        piercings: Object.entries(Piercings(locale)).map(([value, label]) => ({
          value,
          label,
        })),
        tattoos: Object.entries(Tattoos(locale)).map(([value, label]) => ({
          value,
          label,
        })),

        hasSilicon: [
          { value: 'true', label: attendantFilterSiliconYes() },
          { value: 'false', label: attendantFilterSiliconNo() },
        ],
        travelCompanion: [{ value: 'true', label: TravelCompanion(locale) }],
      }),
      [locale]
    );

  const valueMap: Partial<
    Record<
      keyof AttendantSearchParams,
      (locale: Locale) => Record<string, string>
    >
  > = useMemo(
    () => ({
      smoker: SmokerStatuses,
      gos: GosStatuses,
      toys: Toys,
      tattoos: Tattoos,
      piercings: Piercings,
      customers: NoCustomerStatuses,
      visits: VisitStatuses,
      mobility: MobilityStatuses,
    }),
    []
  );

  const activeFilters = useMemo(() => {
    const entries: {
      key: keyof AttendantSearchParams;
      value: string;
      label: string;
    }[] = [];

    for (const [key, value] of Object.entries(filters) as [
      keyof AttendantSearchParams,
      unknown,
    ][]) {
      if (
        value === undefined ||
        [
          'page',
          'lat',
          'lng',
          'limit',
          'city',
          'sortBy',
          'location',
          'duration',
          'start',
          'country',
          'language',
          'name',
          'type',
        ].includes(key)
      )
        continue;

      if (
        (key === 'ageFrom' && value === minimalAge) ||
        (key === 'ageTo' && value === maximalAge) ||
        (key === 'heightFrom' && value === minimalHeight) ||
        (key === 'heightTo' && value === maximalHeight)
      ) {
        continue;
      }

      const labelPrefix = labelMap[key] ?? key;

      const addEntry = (val: string | number | boolean) => {
        let translatedValue = val.toString();

        if (valueMap[key]) {
          translatedValue =
            valueMap[key]!(locale)[val.toString()] ?? val.toString();
        }

        if (arrayLabelMap[key]) {
          const found = arrayLabelMap[key].find(
            (e) => e.value === val.toString()
          );
          if (found) translatedValue = found.label;
        }

        if (key === 'origins') {
          translatedValue =
            countries[val.toString()]?.names?.[locale] ?? val.toString();
        }

        if (key === 'languages') {
          translatedValue =
            languages[val.toString()]?.names?.[locale] ?? val.toString();
        }

        if (
          key === 'ageFrom' ||
          key === 'ageTo' ||
          key === 'heightFrom' ||
          key === 'heightTo'
        ) {
          entries.push({
            key,
            value: val.toString(),
            label: `${labelPrefix}: ${translatedValue}`,
          });
        } else {
          entries.push({
            key,
            value: val.toString(),
            label: `${translatedValue}`,
          });
        }
      };

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        addEntry(value);
      } else if (Array.isArray(value)) {
        value.forEach((v) => {
          if (
            typeof v === 'string' ||
            typeof v === 'number' ||
            typeof v === 'boolean'
          ) {
            addEntry(v);
          }
        });
      }
    }

    return entries;
  }, [filters, locale, labelMap, arrayLabelMap, valueMap]);

  const removeFilter = (key: keyof AttendantSearchParams, value: string) => {
    setFilters((prev) => {
      const current = prev[key];

      if (Array.isArray(current)) {
        const next = current.filter((item) => item.toString() !== value);
        return {
          ...prev,
          page: 1,
          [key]: next.length > 0 ? next : undefined,
        };
      } else {
        const clone = { ...prev };
        delete clone[key];
        return {
          ...clone,
          page: 1,
        };
      }
    });
  };

  if (activeFilters.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {activeFilters.map(({ key, value, label }) => (
        <Badge
          key={`${key}-${value}`}
          variant='default'
          className='flex items-center gap-1'
        >
          <span>{label}</span>
          <Button
            variant='ghost'
            size='icon'
            className='h-4 w-4 p-0 hover:text-destructive'
            onClick={() => removeFilter(key, value)}
          >
            <X className='h-3 w-3' />
          </Button>
        </Badge>
      ))}
    </div>
  );
}
